import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Link2, Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";

import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Checkbox } from "components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import MultiSelect from "components/forms/MultiSelect";
import { cn } from "lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "components/ui/accordion";

const CONTENT_TYPE_OPTIONS = [
  { key: "paragraph", text: "Paragraph", value: "paragraph" },
  { key: "h1", text: "Header 1", value: "h1" },
  { key: "h2", text: "Header 2", value: "h2" },
  { key: "checkbox_text", text: "Text with a checkbox", value: "checkbox_text" },
  { key: "link", text: "Link", value: "link" },
  { key: "image", text: "Image", value: "image" },
];

const LANGUAGE_OPTIONS = [
  { key: "FR", text: "French (FR)", value: "FR" },
  { key: "POR", text: "Portuguese (POR)", value: "POR" },
  { key: "ES", text: "Spanish (ES)", value: "ES" },
];

function normalizeLangCode(code) {
  if (!code) return "";
  return String(code).toUpperCase();
}

function deepCloneScreens(screens) {
  try {
    return JSON.parse(JSON.stringify(screens || []));
  } catch (e) {
    return [];
  }
}

const emptyScreen = () => ({ id: uuidv4(), title: "", contents: [] });

const ContentEditor = ({
  screenId,
  content,
  updateContent,
  removeContent,
  updateSelection,
  insertInlineLink,
  requiredLangs,
  moveContentUp,
  moveContentDown,
  isFirst,
  isLast,
}) => {
  if (!content) return null;
  const { id, type, payload } = content;

  const renderTranslatedInputs = ({ placeholderBase, keyPrefix, component: Comp }) => (
    <div className="space-y-2">
      {(requiredLangs || []).map((lc) => {
        const norm = normalizeLangCode(lc);
        const key = norm;
        return (
          <Comp
            key={`${keyPrefix}-${key}`}
            className="mt-2"
            placeholder={`${placeholderBase} (${lc})`}
            value={payload?.[key] || payload?.[`text_${norm.toLowerCase()}`] || ""}
            onChange={(e) => updateContent(screenId, id, { [key]: e.target.value })}
          />
        );
      })}
    </div>
  );

  if (type === "paragraph") {
    return (
      <Card className="border border-border/60 bg-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-semibold">Paragraph</CardTitle>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => moveContentUp(screenId, id)}
              disabled={isFirst}
              aria-label="Move up"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => moveContentDown(screenId, id)}
              disabled={isLast}
              aria-label="Move down"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeContent(screenId, id)}
              className="text-destructive"
              aria-label="Remove paragraph"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            rows={4}
            placeholder="Enter paragraph text (URLs and emails allowed). New lines preserved."
            value={payload?.text || ""}
            onChange={(e) => updateContent(screenId, id, { text: e.target.value })}
            onSelect={(e) =>
              updateSelection(id, e.target.selectionStart ?? 0, e.target.selectionEnd ?? 0)
            }
          />
          {renderTranslatedInputs({
            placeholderBase: "Paragraph text",
            keyPrefix: `paragraph-${id}`,
            component: (props) => <Textarea rows={3} {...props} />,
          })}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="inline-flex items-center gap-2"
            onClick={() => insertInlineLink(screenId, id, payload?.text || "")}
          >
            <Link2 className="h-4 w-4" /> Insert link
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (type === "h1" || type === "h2") {
    const headerLabel = type === "h1" ? "Header 1" : "Header 2";
    return (
      <Card className="border border-border/60 bg-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-semibold">{headerLabel}</CardTitle>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => moveContentUp(screenId, id)}
              disabled={isFirst}
              aria-label="Move up"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => moveContentDown(screenId, id)}
              disabled={isLast}
              aria-label="Move down"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeContent(screenId, id)}
              className="text-destructive"
              aria-label={`Remove ${headerLabel}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input
            placeholder={`${headerLabel} text`}
            value={payload?.text || ""}
            onChange={(e) => updateContent(screenId, id, { text: e.target.value })}
          />
          {renderTranslatedInputs({
            placeholderBase: `${headerLabel} text`,
            keyPrefix: `${type}-${id}`,
            component: (props) => <Input {...props} />,
          })}
        </CardContent>
      </Card>
    );
  }

  if (type === "link") {
    return (
      <Card className="border border-border/60 bg-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-semibold">Link</CardTitle>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => moveContentUp(screenId, id)}
              disabled={isFirst}
              aria-label="Move up"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => moveContentDown(screenId, id)}
              disabled={isLast}
              aria-label="Move down"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeContent(screenId, id)}
              className="text-destructive"
              aria-label="Remove link"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input
            placeholder="Label (optional)"
            value={payload?.label || ""}
            onChange={(e) => updateContent(screenId, id, { label: e.target.value })}
          />
          {renderTranslatedInputs({
            placeholderBase: "Label",
            keyPrefix: `link-${id}`,
            component: (props) => <Input {...props} />,
          })}
          <Input
            placeholder="URL or email (https://... or name@example.com)"
            value={payload?.url || ""}
            onChange={(e) => updateContent(screenId, id, { url: e.target.value })}
          />
        </CardContent>
      </Card>
    );
  }

  if (type === "checkbox_text") {
    return (
      <Card className="border border-border/60 bg-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-semibold">Text with a checkbox</CardTitle>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => moveContentUp(screenId, id)}
              disabled={isFirst}
              aria-label="Move up"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => moveContentDown(screenId, id)}
              disabled={isLast}
              aria-label="Move down"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeContent(screenId, id)}
              className="text-destructive"
              aria-label="Remove checkbox text"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input
            placeholder="Text"
            value={payload?.text || ""}
            onChange={(e) => updateContent(screenId, id, { text: e.target.value })}
            onSelect={(e) =>
              updateSelection(id, e.target.selectionStart ?? 0, e.target.selectionEnd ?? 0)
            }
          />
          {renderTranslatedInputs({
            placeholderBase: "Text",
            keyPrefix: `checkbox-${id}`,
            component: (props) => <Input {...props} />,
          })}
          <label className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Checkbox
              checked={!!payload?.checkedByDefault}
              onCheckedChange={(checked) =>
                updateContent(screenId, id, { checkedByDefault: !!checked })
              }
            />
            Checked by default
          </label>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="inline-flex items-center gap-2"
            onClick={() => insertInlineLink(screenId, id, payload?.text || "")}
          >
            <Link2 className="h-4 w-4" /> Insert link
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (type === "image") {
    return (
      <Card className="border border-border/60 bg-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-semibold">Image</CardTitle>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => moveContentUp(screenId, id)}
              disabled={isFirst}
              aria-label="Move up"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => moveContentDown(screenId, id)}
              disabled={isLast}
              aria-label="Move down"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeContent(screenId, id)}
              className="text-destructive"
              aria-label="Remove image"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input
            placeholder="Image URL"
            value={payload?.url || ""}
            onChange={(e) => updateContent(screenId, id, { url: e.target.value })}
          />
          <div className="flex items-center gap-2">
            <Input
              type="number"
              placeholder="Height (px)"
              value={payload?.height ?? ""}
              onChange={(e) =>
                updateContent(screenId, id, {
                  height: Number.isNaN(parseInt(e.target.value, 10))
                    ? null
                    : parseInt(e.target.value, 10),
                })
              }
              className="w-32"
            />
            <span className="text-xs text-muted-foreground">pixels</span>
          </div>
          {payload?.url ? (
            <div className="mt-2">
              <img
                src={payload.url}
                alt="Preview"
                style={{ height: payload.height ? `${payload.height}px` : "auto", maxWidth: "100%" }}
                className="rounded border"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  return null;
};

const CustomEndScreensModal = ({ open, onClose, existingScreens, existingLanguages, existingDefaults, onSave }) => {
  const [screens, setScreens] = useState([]);
  const [selections, setSelections] = useState({});
  const [requiredLangs, setRequiredLangs] = useState([]);
  const [expandedScreenId, setExpandedScreenId] = useState(null);

  useEffect(() => {
    if (open) {
      const cloned = deepCloneScreens(existingScreens);
      setScreens(cloned);
      const initLangs = Array.isArray(existingLanguages)
        ? existingLanguages
        : Array.isArray(existingDefaults?.languages)
        ? existingDefaults.languages
        : [];
      setRequiredLangs(initLangs);
      setExpandedScreenId(cloned?.[0]?.id || null);
    }
  }, [open, existingScreens, existingLanguages, existingDefaults]);

  function ensureLangFieldsOnScreens(nextLangs, baseScreens) {
    const langs = (nextLangs || []).map(normalizeLangCode);
    return (baseScreens || []).map((s) => {
      const next = { ...s };
      langs.forEach((lc) => {
        if (next[lc] === undefined && next[lc] !== null) next[lc] = next[lc] || "";
      });
      next.contents = (next.contents || []).map((c) => {
        const payload = { ...(c.payload || {}) };
        if (["paragraph", "h1", "h2", "checkbox_text", "link", "image"].includes(c.type)) {
          langs.forEach((lc) => {
            if (payload[lc] === undefined) payload[lc] = "";
          });
        }
        return { ...c, payload };
      });
      return next;
    });
  }

  function addScreen() {
    const newScreen = emptyScreen();
    setScreens((prev) => {
      const next = ensureLangFieldsOnScreens(requiredLangs, [...(prev || []), newScreen]);
      setExpandedScreenId(newScreen.id);
      return next;
    });
  }

  function removeScreen(screenId) {
    setScreens((prev) => {
      const next = (prev || []).filter((s) => s.id !== screenId);
      if (expandedScreenId === screenId) {
        setExpandedScreenId(next.length ? next[0].id : null);
      }
      return next;
    });
  }

  function updateScreenTitle(screenId, title) {
    setScreens((prev) => (prev || []).map((s) => (s.id === screenId ? { ...s, title } : s)));
  }

  function updateScreenTitleLang(screenId, langCode, title) {
    const norm = normalizeLangCode(langCode);
    setScreens((prev) =>
      (prev || []).map((s) => (s.id === screenId ? { ...s, [norm]: title } : s)),
    );
  }

  function addContent(screenId, type) {
    const baseContent = { id: uuidv4(), type };
    let payload = {};
    if (type === "paragraph") payload = { text: "", link: null };
    if (type === "h1") payload = { text: "" };
    if (type === "h2") payload = { text: "" };
    if (type === "checkbox_text") payload = { text: "", checkedByDefault: false, link: null };
    if (type === "link") payload = { label: "", url: "" };
    if (type === "image") payload = { url: "", height: null };

    payload = ensureLangFieldsOnScreens(requiredLangs, [
      { id: "temp", contents: [{ id: "temp-content", type, payload }] },
    ])[0].contents[0].payload;

    setScreens((prev) =>
      (prev || []).map((s) =>
        s.id === screenId
          ? { ...s, contents: [...(s.contents || []), { ...baseContent, payload }] }
          : s,
      ),
    );
  }

  function removeContent(screenId, contentId) {
    setScreens((prev) =>
      (prev || []).map((s) =>
        s.id === screenId
          ? { ...s, contents: (s.contents || []).filter((c) => c.id !== contentId) }
          : s,
      ),
    );
  }

  function moveContentUp(screenId, contentId) {
    setScreens((prev) =>
      (prev || []).map((s) => {
        if (s.id !== screenId) return s;
        const contents = [...(s.contents || [])];
        const index = contents.findIndex((c) => c.id === contentId);
        if (index > 0) {
          [contents[index - 1], contents[index]] = [contents[index], contents[index - 1]];
        }
        return { ...s, contents };
      }),
    );
  }

  function moveContentDown(screenId, contentId) {
    setScreens((prev) =>
      (prev || []).map((s) => {
        if (s.id !== screenId) return s;
        const contents = [...(s.contents || [])];
        const index = contents.findIndex((c) => c.id === contentId);
        if (index > -1 && index < contents.length - 1) {
          [contents[index], contents[index + 1]] = [contents[index + 1], contents[index]];
        }
        return { ...s, contents };
      }),
    );
  }

  function updateContent(screenId, contentId, update) {
    setScreens((prev) =>
      (prev || []).map((s) => {
        if (s.id !== screenId) return s;
        return {
          ...s,
          contents: (s.contents || []).map((c) =>
            c.id === contentId ? { ...c, payload: { ...c.payload, ...update } } : c,
          ),
        };
      }),
    );
  }

  function updateSelection(contentId, start, end) {
    setSelections((prev) => ({ ...prev, [contentId]: { start: start || 0, end: end || 0 } }));
  }

  function insertInlineLink(screenId, contentId, currentText) {
    const text = currentText || "";
    const sel = selections[contentId] || { start: text.length, end: text.length };
    const start = Math.max(0, Math.min(sel.start, text.length));
    const end = Math.max(0, Math.min(sel.end, text.length));
    const hasSelection = end > start;
    let label = hasSelection ? text.slice(start, end) : window.prompt("Link label (visible text)", "");
    if (label == null) return;
    if (!hasSelection && label.length === 0) return;
    let url = window.prompt("Link URL or email (https://... or name@example.com)", "");
    if (url == null || url.trim().length === 0) return;
    url = url.trim();
    if (/^mailto:/i.test(url)) {
      // keep as provided
    } else if (/^[^\s@]+@[^\s@]+\.[^\s@]+/.test(url)) {
      url = `mailto:${url}`;
    } else if (!/^https?:\/\//i.test(url)) {
      url = `https://${url}`;
    }
    const before = text.slice(0, start);
    const after = text.slice(end);
    const token = `{{${label}|${url}}}`;
    const newText = `${before}${token}${after}`;
    updateContent(screenId, contentId, { text: newText });
  }

  useEffect(() => {
    setScreens((prev) => ensureLangFieldsOnScreens(requiredLangs, prev));
  }, [requiredLangs]);

  function onSaveClick() {
    const normalized = (screens || []).map((s) => ({
      id: s.id || uuidv4(),
      title: s.title || "",
      ...(() => {
        const acc = {};
        (requiredLangs || []).forEach((lc) => {
          const norm = normalizeLangCode(lc);
          if (s[norm] !== undefined && s[norm] !== null) acc[norm] = s[norm];
        });
        return acc;
      })(),
      contents: (s.contents || []).map((c) => ({
        id: c.id || uuidv4(),
        type: c.type,
        payload: c.payload || {},
      })),
    }));
    onSave({ screens: normalized, languages: requiredLangs });
  }

  const handleDialogChange = (nextOpen) => {
    if (!nextOpen) onClose && onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent className="max-h-[90dvh] w-full max-w-6xl overflow-hidden p-0 flex flex-col">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>End Screens</DialogTitle>
          <DialogDescription>
            Add multiple screens and manage their content, including translations.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-1 min-h-0 flex-col gap-6 overflow-y-auto px-6 pb-6 lg:grid lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)] lg:gap-8">
          <section className="space-y-4 rounded-lg border border-border/60 bg-muted/20 p-4">
            <Card className="border-none shadow-none">
              <CardHeader className="px-0">
                <CardTitle className="text-base">Required languages</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 px-0">
                <MultiSelect
                  options={LANGUAGE_OPTIONS}
                  value={requiredLangs}
                  onChange={(value) => setRequiredLangs(value || [])}
                  placeholder="Select languages"
                />
                <p className="text-xs text-muted-foreground">
                  Languages selected here must have translations on titles and content. English is implicit.
                </p>
              </CardContent>
            </Card>
          </section>

          <section className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-border/60">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 px-4 py-3">
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  Screens ({screens?.length || 0})
                </h3>
                <p className="text-xs text-muted-foreground">
                  Add screens, then populate with content blocks.
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={addScreen}
                className="inline-flex items-center gap-2"
              >
                <Plus className="h-4 w-4" /> Add screen
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
              {(screens || []).length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border/60 bg-muted/20 p-8 text-center text-sm text-muted-foreground">
                  <span>No screens yet.</span>
                  <span>Click "Add screen" to create one.</span>
                </div>
              ) : (
                <Accordion
                  type="single"
                  collapsible
                  value={expandedScreenId || undefined}
                  onValueChange={(value) => setExpandedScreenId(value || null)}
                  className="space-y-4"
                >
                  {(screens || []).map((screen, index) => (
                    <AccordionItem key={screen.id} value={screen.id} className="overflow-hidden rounded-lg border border-border/70 bg-card">
                      <AccordionTrigger className="px-4 py-3 text-left hover:no-underline">
                        <div className="flex flex-1 flex-col items-start gap-1 text-sm">
                          <span className="font-semibold text-foreground">Screen {index + 1}</span>
                          <span className="text-xs text-muted-foreground">
                            {screen.title?.trim() ? screen.title : "No title set"}
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={(event) => {
                            event.stopPropagation();
                            removeScreen(screen.id);
                          }}
                          className="ml-2 text-destructive hover:text-destructive"
                          aria-label="Remove screen"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4 pt-2">
                        <div className="space-y-4">
                          <div className="space-y-3">
                            <Input
                              placeholder="Optional screen title"
                              value={screen.title || ""}
                              onChange={(e) => updateScreenTitle(screen.id, e.target.value)}
                            />
                            {(requiredLangs || []).map((lc) => (
                              <Input
                                key={`title-${screen.id}-${lc}`}
                                placeholder={`Optional screen title (${lc})`}
                                value={screen?.[normalizeLangCode(lc)] || ""}
                                onChange={(e) => updateScreenTitleLang(screen.id, lc, e.target.value)}
                              />
                            ))}
                          </div>

                          <div className="space-y-3 rounded-lg border border-dashed border-border/70 p-4">
                            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                              Add content
                            </span>
                            <div className="flex flex-wrap gap-2">
                              {CONTENT_TYPE_OPTIONS.map((option) => (
                                <Button
                                  key={option.value}
                                  type="button"
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => addContent(screen.id, option.value)}
                                >
                                  {option.text}
                                </Button>
                              ))}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Click a content type to insert it into this screen.
                            </p>
                          </div>

                          {(screen.contents || []).length === 0 ? (
                            <div className="rounded-lg border border-dashed border-border/70 p-6 text-center text-sm text-muted-foreground">
                              No content blocks yet.
                            </div>
                          ) : (
                            <div className="space-y-4">
                          {(screen.contents || []).map((content, contentIndex) => (
                                <ContentEditor
                                  key={content.id}
                                  screenId={screen.id}
                                  content={content}
                                  updateContent={updateContent}
                                  removeContent={removeContent}
                                  updateSelection={updateSelection}
                                  insertInlineLink={insertInlineLink}
                                  requiredLangs={requiredLangs}
                              moveContentUp={moveContentUp}
                              moveContentDown={moveContentDown}
                              isFirst={contentIndex === 0}
                              isLast={contentIndex === (screen.contents || []).length - 1}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </div>
          </section>
        </div>

        <DialogFooter className="border-t border-border/60 bg-muted/10 px-6 py-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button type="button" onClick={onSaveClick} className="inline-flex items-center gap-2">
            <Plus className="h-4 w-4" /> Save end screens
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CustomEndScreensModal;


